import {Check} from "@sparkware/app-utils"
import {ServiceBase} from "../ServiceBase";
import {IAbFilterData, IAbFilteringMap} from './ab-filtering.service.interface';
import {AbFilteringStrategy} from './ab-filtering-strategy';
import {IAbFilteringStrategy} from "./ab-filtering-strategy.interface";
import {IAbFilteringService} from "./ab-filtering.service.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {FEATURE_NEW_FILTERING} from "../../../common/const/ab-testing-names";

/*
 * Token structure for this feature:
 *
 * !!! Remember to toggle feature on in web configs (key="AbTesting.enableABFiltering")
 *
 * "AbFiltering": {
 *  "Properties": {
 *    "filters": [
 *      {
 *        "arenaId": "slots"      // optional | AB filter only within this arena
 *        "listKey": "SLOTS_NEW"  // optional | AB filter only within this list
 *        "filterBy": {
 *          "gameCompany" : "888",    //name of the company to filter out
 *          "gameVendor"  : "888",    //name of the vendor to filter out
 *          "gameGroup"   : "poker",  //name of the game group to filter out
 *          "gameTypes"   : [130069, 130093, ...] //array of game types to filter out
 *        }
 *      },
 *      ...
 *    ]
 *  }
 * }
 *
 */

export class AbFilteringService extends ServiceBase implements IAbFilteringService {

  constructor(services: IServiceFactoryExtended) {
    super(services);

    this.reaction(
      () => this.services.user.isAuthorized,
      (isAuthorized) => {
        if (isAuthorized) {
          this._loadAbFilteringData();
        }
      },
      {
        fireImmediately: true
      }
    );
  }

  private _abFilteringData: IAbFilterData[] = [];

  // A map of the arenas and lists affected by AB Filtering
  private _abFiltersMap: IAbFilteringMap = {
    lists   : {},
    arenas  : {},
    general : []
  };

  private _loadAbFilteringData(): void {

    const _abFilteringEnabled: boolean = Check.isNullOrUndefined(this.services.configuration.servicesConfigs.AbTesting.enableABFiltering) ? false : this.services.configuration.servicesConfigs.AbTesting.enableABFiltering;
    const _abFilteringFeature = this.services.sdk.getAbTestingDataForFeature(FEATURE_NEW_FILTERING);

    if (_abFilteringEnabled && _abFilteringFeature?.Properties && Check.isString(_abFilteringFeature.Properties.filters)) {
      try {
        this._abFilteringData = JSON.parse(_abFilteringFeature.Properties.filters);
      } catch (e) {
        this.services.logger.error(`Failed to parse AB Test data for feature "AbFiltering".\n${e}`);
      }
    }

    this._mapAbFilteringData();
  }

  private _mapAbFilteringData(): void {

    if (this._abFilteringData && this._abFilteringData.length > 0) {

      // Map the targeted arenas and lists for easy access
      this._abFilteringData.forEach((abFilterData: IAbFilterData) => {

        // Map the lists level filters
        if (abFilterData.listKey) {
          this._abFiltersMap.lists[abFilterData.listKey] = new AbFilteringStrategy(abFilterData, this.services);
        }

        // Map the arenas level filters
        if (Check.isUndefined(abFilterData.listKey) && abFilterData.arenaId) {
          this._abFiltersMap.arenas[abFilterData.arenaId] = new AbFilteringStrategy(abFilterData, this.services);
        }

        // Filters to be applied globally
        if (Check.isUndefined(abFilterData.listKey) && Check.isUndefined(abFilterData.arenaId)) {
          this._abFiltersMap.general.push(new AbFilteringStrategy(abFilterData, this.services));
        }
      });

      this.services.logger.info(`AB Filtering | Filtering data:\n${JSON.stringify(this._abFilteringData, null, 2)}`);
    }
  }

  private _userHasAbFiltering(): boolean {

    if (this.services.user.isAuthorized && this._abFilteringData && this._abFilteringData.length > 0) {
      return true;
    }
    return false;
  }

  private _listHasAbFiltering(adminRefKey: string): boolean {
    return this._userHasAbFiltering() && (Check.isUndefined(this._abFiltersMap.lists[adminRefKey]) ? false: true);
  }

  private _arenaHasAbFiltering(arenaId: string): boolean {
    return this._userHasAbFiltering() && (Check.isUndefined(this._abFiltersMap.arenas[arenaId]) ? false : true);
  }

  public filterGamesByAbTesting(listAdminKey: string, gameTypes: number[]): number[] {
    try {

      const arenaId = (this.services.routing.currentRoute.data && this.services.routing.currentRoute.data.arenaType) || null;

      // No AB Filtering
      if (Check.isNullOrUndefined(listAdminKey) || !this._userHasAbFiltering() || (!this._listHasAbFiltering(listAdminKey) && !this._arenaHasAbFiltering(arenaId) && this._abFiltersMap.general.length === 0)) {
        return gameTypes;
      }

      // Check for AB Filtering at the list level
      if (this._listHasAbFiltering(listAdminKey)) {

        const filterListWithinArena = this._abFiltersMap.lists[listAdminKey].abFilterData.arenaId;

        // Filter the list anywhere it might be
        if (Check.isNullOrUndefined(filterListWithinArena)) {
          gameTypes = this._abFiltersMap.lists[listAdminKey].applyFiltering(gameTypes);

        // Filter the list only inside the specified arena ID
        } else if (filterListWithinArena && (arenaId === filterListWithinArena)) {
          gameTypes = this._abFiltersMap.lists[listAdminKey].applyFiltering(gameTypes);

        }

      // Check for AB Filtering at the arena level
      } else if (this._arenaHasAbFiltering(arenaId)) {
        gameTypes = this._abFiltersMap.arenas[arenaId].applyFiltering(gameTypes);

      // Check for AB Filtering at the general level
      } else {
        this._abFiltersMap.general.forEach((abFilteringStrategy: IAbFilteringStrategy) => {
          gameTypes = abFilteringStrategy.applyFiltering(gameTypes);
        });
      }

      return gameTypes;

    } catch (e) {
      this.services.logger.error(`AB Filtering failed!\n${e}`);
      return gameTypes;
    }
  }
}
